import { useState } from "react";
import { Phone, Mail, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";
import { SectionHeading } from "@/components/site/ui";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";

export default function Contact() {
    const [form, setForm] = useState({});
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    useSeo({ title: "Contact SUDIGI — Talk to a Growth Expert", description: "Get in touch with the Sudigi team. Request a callback and start growing your business online." });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.mobile || !form.message) { toast.error("Name, mobile and message are required"); return; }
        setLoading(true);
        try {
            const payload = { ...form };
            if (!payload.email) delete payload.email;
            await api.post("/contact", payload);
            setDone(true);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setLoading(false); }
    };

    return (
        <section className="bg-teal-900 py-16 min-h-[70vh]">
            <div className="container-x grid lg:grid-cols-2 gap-12">
                <div>
                    <SectionHeading eyebrow="Contact" title="Let's Grow Your Business Together" subtitle="Have a question or want to discuss your growth plan? Reach out and our team will get back to you." />
                    <div className="mt-8 space-y-4">
                        <a href="tel:+918084478504" className="flex items-center gap-3 card-teal rounded-xl p-4 text-cream"><Phone className="w-5 h-5 text-gold" /> +91 80844 78504</a>
                        <a href="mailto:info@sudigi.in" className="flex items-center gap-3 card-teal rounded-xl p-4 text-cream"><Mail className="w-5 h-5 text-gold" /> info@sudigi.in</a>
                        <a href="https://wa.me/918084478504" target="_blank" rel="noreferrer" className="flex items-center gap-3 card-teal rounded-xl p-4 text-cream"><MessageCircle className="w-5 h-5 text-growth" /> WhatsApp us</a>
                    </div>
                </div>
                <div className="card-teal rounded-2xl p-7">
                    {done ? (
                        <div className="text-center py-10" data-testid="contact-success">
                            <CheckCircle2 className="w-16 h-16 text-growth mx-auto mb-4" />
                            <h3 className="text-2xl font-bold text-cream">Your request has been submitted successfully.</h3>
                            <p className="text-cream-soft mt-3">Our team will reach out to you shortly.</p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <F label="Name *"><Input className="bg-teal-800/50 border-brandborder text-cream" value={form.name || ""} onChange={set("name")} data-testid="contact-name" /></F>
                            <F label="Email"><Input className="bg-teal-800/50 border-brandborder text-cream" value={form.email || ""} onChange={set("email")} data-testid="contact-email" /></F>
                            <F label="Mobile *"><Input className="bg-teal-800/50 border-brandborder text-cream" value={form.mobile || ""} onChange={set("mobile")} data-testid="contact-mobile" /></F>
                            <F label="Message *"><Textarea rows={4} className="bg-teal-800/50 border-brandborder text-cream" value={form.message || ""} onChange={set("message")} data-testid="contact-message" /></F>
                            <button type="submit" disabled={loading} className="btn-gold w-full rounded-full py-3 flex items-center justify-center gap-2" data-testid="contact-submit">
                                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Request a Callback
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}

function F({ label, children }) {
    return <div className="space-y-1.5"><Label className="text-cream-soft text-sm">{label}</Label>{children}</div>;
}
